import { createDemoStore } from '@/demo/core/create-demo-store';
import type { PersonId } from '../../data/types';
import {
  addWatcher,
  approveCurrentStep,
  buildSeedRequests,
  reopenCurrentStep,
  requestChangesOnCurrentStep,
} from './requests-logic';
import type { IntakeDraft } from './intake-scenarios';
import type { ProcurementRequest } from './requests-types';

/** The intake composer: the submitted text and the draft the agent produced. */
export interface ComposerState {
  text: string;
  draft: IntakeDraft | null;
  /** Bumps on every new draft so the draft card remounts fresh. */
  runId: number;
}

interface RequestsState {
  /** Newest request first. */
  requests: ProcurementRequest[];
  composer: ComposerState;
}

export const requestsStore = createDemoStore<RequestsState>(() => ({
  requests: buildSeedRequests(),
  composer: { text: '', draft: null, runId: 0 },
}));

export function useRequests(): ProcurementRequest[] {
  return requestsStore.useStore((s) => s.requests);
}

/** Kept in the store so a draft survives following a link and coming back. */
export function useComposer(): ComposerState {
  return requestsStore.useStore((s) => s.composer);
}

export function setComposerDraft(
  text: string,
  draft: IntakeDraft | null,
): void {
  requestsStore.update((state) => {
    state.composer = {
      text,
      draft,
      runId: state.composer.runId + 1,
    };
  });
}

export function listRequestIds(): string[] {
  return requestsStore.getState().requests.map((r) => r.id);
}

export function addRequest(request: ProcurementRequest): void {
  requestsStore.update((draft) => {
    draft.requests.unshift(request);
  });
}

function updateRequest(
  id: string,
  apply: (req: ProcurementRequest) => void,
): void {
  requestsStore.update((draft) => {
    const req = draft.requests.find((r) => r.id === id);
    if (req) apply(req);
  });
}

/** Add the person as a watcher on a request, for example after joining it. */
export function watchRequest(id: string, personId: PersonId): void {
  updateRequest(id, (req) => addWatcher(req, personId, Date.now()));
}

export function approveRequestStep(id: string): void {
  updateRequest(id, (req) => approveCurrentStep(req, Date.now()));
}

export function requestRequestChanges(id: string, note: string): void {
  updateRequest(id, (req) =>
    requestChangesOnCurrentStep(req, note, Date.now()),
  );
}

export function reopenRequestStep(id: string): void {
  updateRequest(id, (req) => reopenCurrentStep(req, Date.now()));
}

/** Reset the Requests demo to its seed state. */
export function resetRequestsDemo(): void {
  requestsStore.reset();
}
